import React, { useState } from 'react';
import { Variable, ChevronUp, ChevronDown, Search } from 'lucide-react';
import type { VariablesData, CustomVariable } from './types';
import { VARIABLE_GROUPS } from './helpers';

// ── Variables Sidebar Panel ───────────────────────────────────────────────────

interface VariablesSidebarProps {
  variables: VariablesData | undefined;
  customVars: CustomVariable[];
  activeSectionId: string | null;
  onInsert: (token: string) => void;
}

export default function VariablesSidebar({ variables, customVars, activeSectionId, onInsert }: VariablesSidebarProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(true);

  const builtins = variables?.builtin ?? [];
  const lsearch = search.toLowerCase();

  const filteredBuiltins = lsearch
    ? builtins.filter(v => v.key.includes(lsearch) || v.desc.toLowerCase().includes(lsearch))
    : builtins;

  const filteredCustom = lsearch
    ? customVars.filter(v => v.key.includes(lsearch) || v.label.toLowerCase().includes(lsearch))
    : customVars;

  // Group filtered builtins
  const groupedBuiltins = VARIABLE_GROUPS.map(g => ({
    ...g,
    vars: filteredBuiltins.filter(v => g.keys.includes(v.key)),
  })).filter(g => g.vars.length > 0);

  return (
    <div style={{
      border: '1px solid rgba(255,255,255,0.10)',
      borderRadius: 'var(--radius-card)',
      background: 'var(--glass-bg-card)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <button
        onClick={() => setOpen(x => !x)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.5rem 0.625rem',
          background: 'none', border: 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Variable size={13} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
        <span style={{ flex: 1, fontWeight: 600, fontSize: 'var(--text-sm)' }}>Variabili</span>
        <span style={{ fontSize: 10, color: 'var(--color-text-faint)' }}>
          {activeSectionId ? 'click = inserisci' : 'click = copia'}
        </span>
        {open ? <ChevronUp size={12} style={{ color: 'var(--color-text-faint)' }} /> : <ChevronDown size={12} style={{ color: 'var(--color-text-faint)' }} />}
      </button>

      {open && (
        <div style={{ padding: '0 0.625rem 0.625rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', overflowX: 'hidden' }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={11} style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-faint)' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Cerca variabile…"
              style={{
                width: '100%', padding: '0.3rem 0.5rem 0.3rem 24px',
                border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)',
                background: 'var(--glass-bg-card)', color: 'var(--color-text)',
                fontSize: 11,
              }}
            />
          </div>

          {/* Grouped built-ins */}
          {groupedBuiltins.map(g => (
            <div key={g.label}>
              <p style={{ fontSize: 9, fontWeight: 700, color: g.color, textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0.25rem 0 0.2rem' }}>
                {g.label}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.125rem', overflowX: 'hidden' }}>
                {g.vars.map(v => (
                  <button
                    key={v.key}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => onInsert(`{{${v.key}}}`)}
                    title={`${v.desc}\nToken: {{${v.key}}}`}
                    style={{
                      fontSize: 10, fontFamily: 'monospace',
                      padding: '0.1rem 0.3rem', borderRadius: 'var(--radius-interactive)',
                      border: `1px solid ${g.color}30`,
                      background: `${g.color}12`,
                      color: g.color,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    {v.key}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Custom */}
          {filteredCustom.length > 0 && (
            <div>
              <p style={{ fontSize: 9, fontWeight: 700, color: 'var(--color-warning)', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0.25rem 0 0.2rem' }}>
                Personalizzate
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.125rem', overflowX: 'hidden' }}>
                {filteredCustom.map(cv => (
                  <button
                    key={cv.key}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => onInsert(`{{${cv.key}}}`)}
                    title={`${cv.label}\nDefault: ${cv.defaultValue || '—'}\nToken: {{${cv.key}}}`}
                    style={{
                      fontSize: 10, fontFamily: 'monospace',
                      padding: '0.1rem 0.3rem', borderRadius: 'var(--radius-interactive)',
                      border: '1px solid var(--color-warning)',
                      background: 'var(--color-warning-highlight)',
                      color: 'var(--color-warning)',
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    {cv.key}
                  </button>
                ))}
              </div>
            </div>
          )}

          {groupedBuiltins.length === 0 && filteredCustom.length === 0 && (
            <p style={{ fontSize: 11, color: 'var(--color-text-faint)', textAlign: 'center', padding: '0.5rem 0' }}>
              Nessuna variabile trovata
            </p>
          )}
        </div>
      )}
    </div>
  );
}
